import {
  ClearwaterTransaction,
  ExternalDataSourceType,
  ExternalTransaction,
  MatchError,
  MatchErrorMessage,
  MatchUsingExternalError,
  MatchUsingExternalErrorMessage,
} from '../models'
import {
  checkAlreadyMatched,
  checkIsSameSource,
  checkSomeAvailableActionsIncludes,
  isTransactionCategorySupported,
  isTransactionSameCategory,
} from './clearwater-transactions-utils'
import { checkSomeExtTxIncludesAvailableAction } from './external-transactions-utils'

/**
 * Test match between clearwater transactions and external transactions,
 * which results in a list of locally known errors which occur with the match.
 */
export function testMatch(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): MatchError[] {
  // Test for errors in a priority-order to account for overlapping errors
  // i.e. ManyToManyUnsupported comes before SameSource
  // Test for errors in a priority-order to account for overlapping errors
  // i.e. ManyToManyUnsupported comes before SameSource
  const errors = []
  const hasMultiSourceExternalTransactions =
    extTrans.filter(
      exTrans => exTrans.dataSourceType === ExternalDataSourceType.CUSTODY,
    ).length > 1 ||
    extTrans.filter(
      exTrans => exTrans.dataSourceType === ExternalDataSourceType.TRADING,
    ).length > 1
  const isManyToManyMatchUnavailable = !checkSomeAvailableActionsIncludes(
    cwTrans,
    'manyToManyMatch',
  )
  if (hasMultiSourceExternalTransactions && isManyToManyMatchUnavailable) {
    errors.push(MatchError.ManyToManyUnsupported)
  }

  if (extTrans.length === 0) {
    errors.push(MatchError.NoExtTxSelected)
  }

  if (cwTrans.length === 0) {
    errors.push(MatchError.NoCwTxSelected)
  }

  const isSameTransaction =
    cwTrans.length > 0 &&
    extTrans.length > 0 &&
    cwTrans.some(cwTx =>
      extTrans.some(extTx => checkAlreadyMatched(cwTx, extTx)),
    )
  if (isSameTransaction) {
    errors.push(MatchError.SameTransaction)
  }

  const isSameSource =
    cwTrans.length > 0 &&
    extTrans.length > 0 &&
    cwTrans.some(cwTx => extTrans.some(extTx => checkIsSameSource(cwTx, extTx)))
  if (isSameSource) {
    errors.push(MatchError.SameSource)
  }

  // return errors, sorted
  return errors.sort()
}
/**
 *Finding Errors in Match Using External option
 */
export function testMatchUsingExternal(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
) {
  const errors = []

  if (extTrans.length === 0) {
    errors.push(MatchUsingExternalError.NoExtTxSelected)
  }
  if (cwTrans.length === 0) {
    errors.push(MatchUsingExternalError.NoCwTxSelected)
  }

  let isCategorySupported
  if (cwTrans.length > 0 && extTrans.length == 1) {
    isCategorySupported = isTransactionCategorySupported(
      cwTrans[0],
      extTrans[0],
    )
  }
  if (!isCategorySupported) {
    errors.push(MatchUsingExternalError.IsCategorySupported)
  }
  let isSameCategory
  if (cwTrans.length > 0 && extTrans.length == 1) {
    isSameCategory = isTransactionSameCategory(cwTrans[0], extTrans[0])
  }

  if (!isSameCategory) {
    errors.push(MatchUsingExternalError.DiffCategoryTrans)
  }
  testCwOnlyErrors(cwTrans, extTrans[0], errors)
  return errors.sort()
}
/**
 * test if action to be taken is many to many if option is open and we have either additional match
 *
 * @param cwTrans
 * @param extTrans
 */

/**
 * test if action to be taken is many to many if option is open and we have either additional match or multi same source match
 *
 * @param cwTrans cwTrans
 * @param extTrans extTrans
 */
export function isManyToManyMatch(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
) {
  const isManyToManyMatchAvailable = checkSomeAvailableActionsIncludes(
    cwTrans,
    'manyToManyMatch',
  )

  const isSameSource =
    cwTrans.length > 0 &&
    extTrans.length > 0 &&
    cwTrans.some(cwTx => extTrans.some(extTx => checkIsSameSource(cwTx, extTx)))

  const hasMultiSourceExternalTransactions =
    extTrans.filter(
      exTrans => exTrans.dataSourceType === ExternalDataSourceType.CUSTODY,
    ).length > 1 ||
    extTrans.filter(
      exTrans => exTrans.dataSourceType === ExternalDataSourceType.TRADING,
    ).length > 1

  return (
    isManyToManyMatchAvailable &&
    (isSameSource || hasMultiSourceExternalTransactions)
  )
}
/**
 * Get tooltip string for match action
 */
export function getMatchErrorMessage(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): MatchErrorMessage | undefined {
  const errors = testMatch(cwTrans, extTrans)

  if (errors.length === 0) {
    return undefined
  }

  // Only consider the first error
  switch (errors[0]) {
    case MatchError.SameTransaction:
      return MatchErrorMessage.SameTransaction
    case MatchError.SameSource:
      return MatchErrorMessage.SameSource
    case MatchError.NoExtTxSelected:
      return MatchErrorMessage.NoExtTxSelected
    case MatchError.NoCwTxSelected:
      return MatchErrorMessage.NoCwTxSelected
    case MatchError.ManyToManyUnsupported:
      return MatchErrorMessage.ManyToManyUnsupported
  }
}
/**
 * Get Error Message for Match Using External Data
 */
export function getMatchUsingExternalErrorMessage(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): MatchUsingExternalErrorMessage | undefined {
  const errors = testMatchUsingExternal(cwTrans, extTrans)

  if (errors.length === 0) {
    return undefined
  }

  // Only consider the first error
  switch (errors[0]) {
    case MatchUsingExternalError.NoExtTxSelected:
      return MatchUsingExternalErrorMessage.NoExtTxSelected
    case MatchUsingExternalError.NoCwTxSelected:
      return MatchUsingExternalErrorMessage.NoCwTxSelected
    case MatchUsingExternalError.IsCategorySupported:
      return MatchUsingExternalErrorMessage.IsCategorySupported
    case MatchUsingExternalError.DiffCategoryTrans:
      return MatchUsingExternalErrorMessage.DiffCategoryTrans
    case MatchUsingExternalError.SameSource:
      return MatchUsingExternalErrorMessage.SameSource
    case MatchUsingExternalError.DiffAccount:
      return MatchUsingExternalErrorMessage.DiffAccount
    case MatchUsingExternalError.DiffTransType:
      return MatchUsingExternalErrorMessage.DiffTransType
    case MatchUsingExternalError.DiffEntryDate:
      return MatchUsingExternalErrorMessage.DiffEntryDate
    case MatchUsingExternalError.DiffAssetId:
      return MatchUsingExternalErrorMessage.DiffAssetId
  }
}

/**
 * Check if match is disabled across the given transactions.
 */
export function checkIsMatchDisabled(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): boolean {
  const isMatchUnavailable =
    !checkSomeAvailableActionsIncludes(cwTrans, 'match') ||
    !checkSomeExtTxIncludesAvailableAction(extTrans, 'match')
  const errors = testMatch(cwTrans, extTrans)
  return isMatchUnavailable || errors.length > 0
}
/**
 *Check if match using external is disabled across the given transactions
 */
export function checkIsMatchUsingExternalDisabled(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): boolean {
  const isMatchUsingExternalUnavailable =
    !checkSomeAvailableActionsIncludes(cwTrans, 'matchUsingExternal') ||
    !checkSomeExtTxIncludesAvailableAction(extTrans, 'matchUsingExternal')

  const errors = testMatchUsingExternal(cwTrans, extTrans)

  //isTransactionSameCategory() will also check that Transaction Belong to Cash and Trade only
  const numberSelectedTran = extTrans.length > 1

  return (
    isMatchUsingExternalUnavailable || numberSelectedTran || errors.length > 0
  )
}
/**
 * Check if match is disabled across the given transactions.
 */
export function checkIsMatchAdditionalDisabled(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): boolean {
  const isManyToManyMatchAvailable = checkSomeAvailableActionsIncludes(
    cwTrans,
    'manyToManyMatch',
  )
  const isSameSource =
    cwTrans.length > 0 &&
    extTrans.length > 0 &&
    cwTrans.some(cwTx => extTrans.some(extTx => checkIsSameSource(cwTx, extTx)))

  const isAlreadyMatchedToSameTransaction = cwTrans.some(cwTx =>
    extTrans.some(extTx => checkAlreadyMatched(cwTx, extTx)),
  )

  return (
    !isManyToManyMatchAvailable ||
    !isSameSource ||
    isAlreadyMatchedToSameTransaction
  )
}
/**
 * testCwOnlyErrors tests cw only errors
 */
function testCwOnlyErrors(
  cwTrans: ClearwaterTransaction[],
  extTransaction: ExternalTransaction,
  errors: MatchUsingExternalError[],
) {
  const accountIds = new Set<number>()
  const assetIds = new Set<string>()
  const entryDates = new Set<string>()
  const tranTypeCodes = new Set<string>()

  cwTrans.forEach(transaction => {
    accountIds.add(transaction.accountId)
    assetIds.add(transaction.secIdentifier ?? '')
    entryDates.add(transaction.entryDate ?? '')
    tranTypeCodes.add(transaction.tranTypeCode)
  })
  if (accountIds.size > 1) {
    errors.push(MatchUsingExternalError.DiffAccount)
  }
  if (tranTypeCodes.size > 1) {
    errors.push(MatchUsingExternalError.DiffTransType)
  }
  if (entryDates.size > 1) {
    errors.push(MatchUsingExternalError.DiffEntryDate)
  }
  if (assetIds.size > 1) {
    errors.push(MatchUsingExternalError.DiffAssetId)
  }

  if (
    extTransaction &&
    cwTrans.some(cwTx => checkIsSameSource(cwTx, extTransaction))
  ) {
    errors.push(MatchUsingExternalError.SameSource)
  }
}
